import React, { useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { labelStyle } from "./commonStyles";
import { canDeleteSharedPermissions, getPermissions } from "./dmsPermissions";

const FolderList = ({
    handleFolderClick,
    selectedFolderId,
    setSelectedFolderId,
    folders,
    setSelectedDocId,
    onRenameClick,
    loadingFolderId,
    expandedFolderIds,
    setExpandedFolderIds,
    onCreateFolder,
    setSelectedFolderName,
    onShareClick,
    onManageFolderPermission,
    permissionList,
    userEmail,
    onDeleteFolder,
    isAdmin
}) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeFolder, setActiveFolder] = useState(null);

    const handleOpenMenu = (event, folder) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setActiveFolder(folder);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveFolder(null);
    };

    const handleRename = () => {
        onRenameClick(activeFolder);
        handleCloseMenu();
    };

    const addNewFolder = () => {
        onCreateFolder(activeFolder)
        handleCloseMenu();
    }

    // const checkPermission = (folderId) => {
    //     const findedItem = permissionList?.find(item => item.resourceId === folderId && item.permissionType !== "View")
    //     if (findedItem) {
    //         return true;
    //     }
    //     return false;
    // }

    const handleShare = () => {
        onShareClick(activeFolder);
        handleCloseMenu();
    }

    const handleDeletePermission = () => {
        // onFolderPermissionDeleteClick(activeFolder);
        // handleCloseMenu();
        onManageFolderPermission(activeFolder);
        handleCloseMenu();
    }

    const handleDeleteFolder = () => {
        onDeleteFolder(activeFolder);
        handleCloseMenu();
    }
    //console.log("folderlist", userEmail, activeFolder);
    const activePerms = getPermissions(activeFolder?.folderId, "Folder", permissionList, userEmail, isAdmin);

    const canDelete = canDeleteSharedPermissions(activeFolder?.folderId, "Folder", permissionList, userEmail, isAdmin);

    const renderTree = (node) => {
        const isLoading = loadingFolderId === node.folderId;
        const perms = getPermissions(
            node.folderId,
            "Folder",
            permissionList,
            userEmail,
            isAdmin
        );
        const hasActions =
            perms.canAddFolder ||
            perms.canManagePermission;
        //console.log("loading", isLoading, loadingFolderId, node.folderId, lazyLoading)
        return <TreeItem
            key={node.folderId}
            itemId={node.folderId}
            label={
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        width: "100%",
                        "& .folder-actions": { visibility: "hidden" },
                        "&:hover .folder-actions": { visibility: node.parentFolderId === null ? "visible" : hasActions ? "visible" : "hidden" },
                    }}
                >
                    <FolderOutlinedIcon sx={{ fontSize: 16, color: node.parentFolderId === null ? "#2563EB" : "#64748B", flexShrink: 0 }} />
                    <Typography
                        title={node.folderName}
                        noWrap
                        sx={{
                            minWidth: 0,
                            flex: 1,
                            fontFamily: "Outfit, sans-serif",
                            fontSize: "12px",
                            lineHeight: "18px",
                            fontWeight: node.parentFolderId === null ? 600 : 400,
                            color: "#172033",
                        }}
                    >
                        {node.folderName}
                    </Typography>
                    {isLoading && <CircularProgress size={14} sx={{ color: "#00344C" }} />}
                    <IconButton
                        size="small"
                        className="folder-actions"
                        onClick={(e) => handleOpenMenu(e, node)}
                        sx={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                        <MoreVertIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                </Box>
            }
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFolderClick(node);
                setSelectedFolderName(node?.folderName)
            }}
        >
            {Array.isArray(node.children)
                ? node.children.map((child) => renderTree(child))
                : null}
        </TreeItem>
    };

    return (
        <Paper
            sx={{
                width: "20%",
                minWidth: 240,
                p: 1.5,
                borderRadius: "3px",
                overflow: "auto",
                border: "1px solid #E2E8F0"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={labelStyle}>Folders</Typography>
                <Typography sx={{ fontSize: "11px", color: "#64748B" }}>{folders.length} top-level</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />

            <SimpleTreeView
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expandedItems={expandedFolderIds}
                onExpandedItemsChange={(event, itemIds) => {
                    setExpandedFolderIds(itemIds);
                }}
                //defaultExpanded={ }
                //selected={selectedFolderId}
                //disabledItemsFocusable={false}
                selectedItems={selectedFolderId || null}
                // onNodeSelect={(_, nodeId) => {
                //   // In MVP, we only load files for leaf folders we have mock data for.
                //   console.log("nodeID", nodeId)
                //   setSelectedFolderId(nodeId);
                //   setSelectedDocId(null);
                // }}
                // onItemClick={(clicked, itemId) => {
                //     console.log("clicked", clicked.target, itemId)
                //     setSelectedFolderId(itemId)
                //     setSelectedDocId(null);
                //     //handleFolderClick(itemId)
                // }}
                onSelectedItemsChange={(event, itemId) => {
                    if (itemId) {
                        setSelectedFolderId(itemId);
                        setSelectedDocId(null);
                    }
                }}
                sx={{
                    "& .MuiTreeItem-content": {
                        minHeight: 32,
                        px: 0.5,
                        py: 0.25,
                        borderRadius: "3px",
                        mb: 0.25,
                        border: "1px solid transparent",
                        "&:hover": { backgroundColor: "#F4F7FB" },
                    },
                    "& .MuiTreeItem-content.Mui-selected": {
                        backgroundColor: "#EAF2FF",
                        borderColor: "#BFDBFE",
                    },
                    "& .MuiTreeItem-content.Mui-selected:hover": {
                        backgroundColor: "#EAF2FF",
                    },
                    "& .MuiTreeItem-iconContainer": {
                        width: 18,
                        color: "#64748B",
                    },
                    "& .MuiTreeItem-groupTransition": {
                        ml: 1.1,
                        pl: 1.4,
                        borderLeft: "1px solid #E2E8F0",
                    },
                    "& .MuiTreeItem-content.Mui-focused:not(.Mui-selected)": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                {folders.map((n) => renderTree(n))}
            </SimpleTreeView>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
            >
                {(activeFolder?.parentFolderId === null || activePerms?.canAddFolder) && <MenuItem onClick={addNewFolder} sx={{ fontSize: "13px" }}>
                    Create Folder
                </MenuItem>}
                {activePerms?.canManagePermission && <MenuItem onClick={handleRename} sx={{ fontSize: "13px" }}>
                    Rename Folder
                </MenuItem>}
                {activePerms?.canManagePermission && <MenuItem onClick={handleShare} sx={{ fontSize: "13px" }}>
                    Share Folder
                </MenuItem>}
                {canDelete && <MenuItem onClick={handleDeletePermission} sx={{ fontSize: "13px" }}>
                    Manage Permission
                </MenuItem>}
                {activePerms?.canDeleteFolder && <MenuItem onClick={handleDeleteFolder} sx={{ fontSize: "13px", color: "#b42318" }}>
                    Delete Folder
                </MenuItem>}
            </Menu>

        </Paper>
    );
};

export default FolderList;
